/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { ctx } from '@/util/context'
import { DM_ENABLED } from '@/messaging/dm/dmConfig'

const { errorHandler } = ctx('upsellFire')

/**
 * Which upsell a transport gate raises on denial. Kept as plain string kinds
 * (not the DM trigger/intent enums) so callers on the boot-eager transport
 * chunk don't statically pull `messaging/dm/types` — the enum values are only
 * dereferenced inside the lazy import below.
 */
export type UpsellKind = 'skipLimit' | 'featureUnavailable'
export type UpsellIntent = 'rewind' | 'scrub'

/**
 * Fire a Dynamic Messaging upsell from a transport gate site (skip / previous /
 * scrub denial). The DM engine + its REST/chrome subtree is boot-heavy, so it
 * and the trigger enum are dynamic-imported here rather than statically pulled
 * into the transport chunk — mirroring the header pill's lazy engine load. Gated
 * on DM_ENABLED so nothing loads or fetches while DM is dormant. Fire-and-forget:
 * the engine owns its own outcome (per-trigger cache policy, critical fallback),
 * and any rejection (including the import) routes to the module error handler.
 */
export function fireUpsell(kind: UpsellKind, intent?: UpsellIntent): void {
    // eslint-disable-next-line no-unnecessary-condition
    if (!DM_ENABLED) return
    Promise.all([import('@/messaging/dm/dynamicMessagingEngine'), import('@/messaging/dm/types')])
        .then(([engineMod, typesMod]) => {
            const { DynamicMessageTrigger, DynamicMessageIntentID } = typesMod
            const trigger =
                kind === 'skipLimit'
                    ? DynamicMessageTrigger.SKIP_LIMIT_REACHED
                    : DynamicMessageTrigger.FEATURE_UNAVAILABLE
            const intentID =
                intent === 'rewind'
                    ? DynamicMessageIntentID.REWIND
                    : intent === 'scrub'
                      ? DynamicMessageIntentID.SCRUB
                      : undefined
            return engineMod.dynamicMessagingEngine.showDynamicMessageForTrigger(
                trigger,
                intentID ? { intentID } : {}
            )
        })
        .catch(errorHandler)
}

/**
 * The transport skip button is a premium affordance for a capped (FREE/PRIME)
 * customer, so it fires the proactive `ON_PREMIUM_FEATURE` nudge — `'load'`
 * when the button mounts, `'click'` on a permitted skip. This is DISTINCT from
 * the skip-DENIAL fires above ({@link fireUpsell} → SKIP_LIMIT_REACHED /
 * FEATURE_UNAVAILABLE), which the guard raises only when a skip is refused: the
 * two never fire for the same gesture because the click nudge fires only on the
 * allowed branch. The tier/age audience gate lives in the lazy-loaded
 * {@link firePremiumFeature} so it can never fire for UNLIMITED.
 */
export type SkipPremiumFeature = 'load' | 'click'

// Session-once guard for the mount ('load') fire: the skip button renders in
// both the mini and full transport, so its onConnect runs more than once — the
// "affordance appeared" nudge should still fire at most once per session.
let skipLoadFired = false

export function fireSkipPremiumFeature(surface: SkipPremiumFeature): void {
    // eslint-disable-next-line no-unnecessary-condition
    if (!DM_ENABLED) return
    if (surface === 'load') {
        if (skipLoadFired) return
        skipLoadFired = true
    }
    import('@/messaging/dm/premiumFeatureFire')
        .then((m) => m.fireSkipButtonPremiumFeature(surface))
        .catch(errorHandler)
}
